import React, { useState } from "react";
import { useMyself } from "@api/v1/hooks/useMyself";
import { useChartDataByInvestment } from "@features/fundamentals/hooks/chartData/useChartDataByInvestment";
import { useChartData } from "@features/fundamentals/hooks/chartData/useChartData";
import { useFakeData } from "@features/fundamentals/hooks/useFakeData";
import { RangeValue } from "@rentablo/components";
import { ComparisonChartWithFilters } from "./ComparisonChartWithFilters";
import { getCurrency } from "./helpers/getCurrency";

interface Value {
  range?: RangeValue;
  secondMetricKey?: string;
}

type Props = {
  investmentId?: number;
  isin?: string;
  quoteProvider?: string;
  tickerSymbol?: string;
  defaultValue?: Value;
  mainMetricKey: string;
  onHide: () => void;
  onChange?: ({ range, secondMetricKey }: Value) => void;
};

export const ComparisonChart = ({
  defaultValue = { range: "5y" },
  mainMetricKey,
  onHide,
  onChange,
  investmentId,
  isin,
  quoteProvider,
  tickerSymbol,
}: Props) => {
  const { data: user, isFetching: isUserLoading } = useMyself();
  const [range, setRange] = useState<RangeValue>(defaultValue.range || "5y");
  const [secondMetricKey, setSecondMetricKey] = useState<string | undefined>(
    defaultValue.secondMetricKey
  );

  const loadByInvestment = !!investmentId;

  const {
    data: dataByInvestment,
    securityData: securityDataByInvestment,
    isFetching: isChartDataByInvestmentLoading,
  } = useChartDataByInvestment(
    {
      investmentId,
      range,
      dataKey1: mainMetricKey,
      dataKey2: secondMetricKey,
    },
    { enabled: !!user && user.isUserPro && loadByInvestment }
  );

  const {
    data: dataByIsin,
    securityData: securityDataByIsin,
    isFetching: isChartDataByIsinLoading,
  } = useChartData(
    {
      isin,
      quoteProvider,
      tickerSymbol,
      range,
      dataKey1: mainMetricKey,
      dataKey2: secondMetricKey,
    },
    { enabled: !!user && user.isUserPro && !loadByInvestment }
  );

  const { data: fakeData } = useFakeData({
    enabled: !!user && !user.isUserPro,
  });

  const isFetching =
    isChartDataByInvestmentLoading || isChartDataByIsinLoading || isUserLoading;

  const handleRangeSelect = (value: RangeValue) => {
    setRange(value);
    onChange && onChange({ range: value, secondMetricKey });
  };

  const handleMetricSelected = (value?: string) => {
    setSecondMetricKey(value);
    onChange && onChange({ range, secondMetricKey: value });
  };

  const data = loadByInvestment ? dataByInvestment : dataByIsin;
  const securityData = loadByInvestment
    ? securityDataByInvestment
    : securityDataByIsin;

  const metricCurrency1 = getCurrency(mainMetricKey, securityData?.currency);
  const metricCurrency2 = getCurrency(secondMetricKey, securityData?.currency);

  const noData = !isFetching && (!data || data.length === 0);

  const chartData = (user?.isUserPro ? data : fakeData) ?? [];

  return (
    <ComparisonChartWithFilters
      noData={noData}
      metricCurrency1={metricCurrency1}
      metricCurrency2={metricCurrency2}
      quoteCurrency="EUR"
      isFetching={isFetching}
      defaultRange={range}
      onHide={onHide}
      data={chartData}
      dataKey1={mainMetricKey}
      dataKey2={secondMetricKey}
      onRangeSelect={handleRangeSelect}
      onMetricSelected={handleMetricSelected}
      onlyPro={user?.isUserPro}
    />
  );
};
